"use server";

// ============================================================
// CLUBHUB – Mitglieder speichern (Server Actions)
// ============================================================
// Ohne Sitzung (Demo-Modus, Musterbestand) wird nichts gespeichert,
// die Masken melden nur Erfolg — wie bisher. Mit Sitzung schreiben die
// Actions in den Verein der Sitzung, nie in einen aus dem Formular.
//
// Historie statt Ueberschreiben: Gruppen, Beitrag und Status bekommen
// beim Wechsel ein Ende und einen neuen Zeitraum; Einwilligungen eine
// neue Zeile in `Consent`. Jede Aenderung steht im Verlauf der Person
// (`ContactActivity`) und im AuditLog — dort nur die Feldnamen, keine
// Inhalte.
// ============================================================
import { revalidatePath } from "next/cache";
import { aktuell } from "@/lib/current";
import type { ActionResult } from "@/lib/action-result";
import { pruefeMitglied, trenneHausnummer } from "@/lib/mitglieder/pruefung";
import {
  CommunicationChannel,
  ContactKind,
  ContactStatus,
  Gender,
  PaymentMethod,
  Salutation,
  type ConsentPurpose,
} from "@/generated/prisma/enums";

const MITGLIED_STATUS = new Set<string>(["ACTIVE", "PASSIVE", "HONORARY"]);
/** Fassung des Einwilligungstexts im Verwaltungsformular. */
const EINWILLIGUNG_FASSUNG = "verwaltung-2026-10";

class NichtGefunden extends Error {}

function text(daten: FormData, feld: string): string | null {
  const wert = daten.get(feld);
  if (typeof wert !== "string") return null;
  const t = wert.trim();
  return t ? t : null;
}

function aus<T extends Record<string, string>>(werte: T, roh: string | null, vorgabe: T[keyof T] | null): T[keyof T] | null {
  if (roh && Object.values(werte).includes(roh)) return roh as T[keyof T];
  return vorgabe;
}

function kalendertag(roh: string | null): Date | null {
  return roh && /^\d{4}-\d{2}-\d{2}$/.test(roh) ? new Date(`${roh}T00:00:00Z`) : null;
}

function heuteUtc(): Date {
  const j = new Date();
  return new Date(Date.UTC(j.getUTCFullYear(), j.getUTCMonth(), j.getUTCDate()));
}

function vortag(d: Date): Date {
  return new Date(d.getTime() - 86_400_000);
}

async function schreibZugang() {
  const { requirePermission, Forbidden } = await import("@/lib/tenant");
  try {
    return { ok: true as const, ctx: await requirePermission("members.write") };
  } catch (e) {
    if (e instanceof Forbidden) return { ok: false as const, fehler: "Dafür fehlt dir das Recht, Mitglieder zu bearbeiten." };
    throw e;
  }
}

function neuLaden(id?: string) {
  revalidatePath("/mitglieder", "layout");
  revalidatePath("/");
  if (id) revalidatePath(`/mitglieder/${id}`, "layout");
}

export async function mitgliedSpeichern(kontaktId: string | null, daten: FormData): Promise<ActionResult<{ id: string }>> {
  const f = pruefeMitglied(daten);
  const erstes = Object.keys(f)[0];
  if (erstes) return { error: f[erstes]!, field: erstes };

  if ((await aktuell()).quelle === "muster") return { success: true, id: kontaktId ?? "c-0001" };
  const z = await schreibZugang();
  if (!z.ok) return { error: z.fehler };
  const { clubId, userId } = z.ctx;
  const { db } = await import("@/lib/db");

  const kind = aus(ContactKind, text(daten, "kind"), "PERSON")!;
  const status = aus(ContactStatus, text(daten, "status"), "ACTIVE")!;
  const adresse = trenneHausnummer(text(daten, "street") ?? "");
  const felder = {
    kind,
    salutation: aus(Salutation, text(daten, "salutation"), null),
    title: text(daten, "title"),
    gender: aus(Gender, text(daten, "gender"), null),
    firstName: kind === "PERSON" ? text(daten, "firstName") : null,
    lastName: kind === "PERSON" ? text(daten, "lastName") : null,
    organizationName: kind === "PERSON" ? null : text(daten, "organizationName"),
    birthDate: kalendertag(text(daten, "birthDate")),
    email: text(daten, "email")?.toLowerCase() ?? null,
    mobile: text(daten, "mobile"),
    phone: text(daten, "phone"),
    communication: aus(CommunicationChannel, text(daten, "communication"), "EMAIL")!,
    street: adresse.street,
    houseNumber: adresse.houseNumber,
    zip: text(daten, "zip"),
    city: text(daten, "city"),
    country: (text(daten, "country") ?? "DE").toUpperCase().slice(0, 2),
    status,
    memberNumber: text(daten, "memberNumber"),
    entryDate: kalendertag(text(daten, "entryDate")),
    consentNewsletter: daten.get("consentNewsletter") === "true",
    consentPhotos: daten.get("consentPhotos") === "true",
    consentInternet: daten.get("consentInternet") === "true",
  };
  const feeTypeId = text(daten, "feeTypeId");
  const zahlart = aus(PaymentMethod, text(daten, "paymentMethod"), "TRANSFER")!;
  const gruppenWunsch = [...new Set(daten.getAll("groupIds").map(String).filter(Boolean))];
  const heute = heuteUtc();
  const gestern = vortag(heute);

  try {
    const id = await db.$transaction(async (tx) => {
      const vorher = kontaktId
        ? await tx.contact.findFirst({ where: { id: kontaktId, clubId }, include: { groups: true, fees: true } })
        : null;
      if (kontaktId && !vorher) throw new NichtGefunden();

      const geaendert = vorher
        ? (Object.keys(felder) as (keyof typeof felder)[]).filter((k) => {
            const a = vorher[k];
            const b = felder[k];
            return a instanceof Date || b instanceof Date ? (a as Date | null)?.getTime() !== (b as Date | null)?.getTime() : a !== b;
          })
        : (Object.keys(felder) as (keyof typeof felder)[]).filter((k) => felder[k] !== null && felder[k] !== false);

      const c = vorher
        ? await tx.contact.update({ where: { id: vorher.id }, data: felder })
        : await tx.contact.create({ data: { clubId, ...felder } });

      // Gruppen: nur Gruppen dieses Vereins; Abgaenge bekommen ein Ende, Zugaenge einen Anfang.
      const gueltig = new Set(
        (await tx.group.findMany({ where: { clubId, id: { in: gruppenWunsch } }, select: { id: true } })).map((g) => g.id),
      );
      const aktiv = (vorher?.groups ?? []).filter((m) => !m.until || m.until >= heute);
      for (const m of aktiv) {
        if (gueltig.has(m.groupId)) continue;
        if (m.since && m.since >= heute) await tx.groupMembership.delete({ where: { id: m.id } });
        else await tx.groupMembership.update({ where: { id: m.id }, data: { until: gestern } });
      }
      const schonDa = new Set(aktiv.map((m) => m.groupId));
      for (const groupId of gueltig) {
        if (!schonDa.has(groupId)) await tx.groupMembership.create({ data: { groupId, contactId: c.id, since: heute } });
      }

      // Beitrag: Wechsel der Beitragsart beendet die alte Zuordnung.
      const art = feeTypeId ? await tx.feeType.findFirst({ where: { id: feeTypeId, clubId }, select: { id: true } }) : null;
      const laufend = (vorher?.fees ?? []).find((b) => b.validFrom <= heute && (!b.validUntil || b.validUntil >= heute)) ?? null;
      if ((art?.id ?? null) !== (laufend?.feeTypeId ?? null)) {
        if (laufend) {
          if (laufend.validFrom >= heute) await tx.contactFee.delete({ where: { id: laufend.id } });
          else await tx.contactFee.update({ where: { id: laufend.id }, data: { validUntil: gestern } });
        }
        if (art) {
          await tx.contactFee.create({ data: { clubId, contactId: c.id, feeTypeId: art.id, validFrom: heute, paymentMethod: zahlart } });
        }
      } else if (laufend && laufend.paymentMethod !== zahlart) {
        await tx.contactFee.update({ where: { id: laufend.id }, data: { paymentMethod: zahlart } });
      }

      // Status als Zeitraum (Bestandserhebung, Ehrungen).
      if (!vorher || vorher.status !== status) {
        await tx.membershipPeriod.updateMany({
          where: { clubId, contactId: c.id, validUntil: null },
          data: { validUntil: gestern },
        });
        await tx.membershipPeriod.create({
          data: { clubId, contactId: c.id, status, validFrom: vorher ? heute : (felder.entryDate ?? heute) },
        });
      }

      // Einwilligungen: jede Aenderung eine neue Zeile, nur anhaengen.
      const zwecke: [keyof typeof felder, ConsentPurpose][] = [
        ["consentNewsletter", "NEWSLETTER"],
        ["consentPhotos", "PHOTOS_INTERNAL"],
        ["consentInternet", "INTERNET"],
      ];
      let einwilligung = false;
      for (const [feld, purpose] of zwecke) {
        const neu = felder[feld] as boolean;
        if (vorher ? vorher[feld] !== neu : neu) {
          einwilligung = true;
          await tx.consent.create({
            data: { clubId, contactId: c.id, purpose, granted: neu, textVersion: EINWILLIGUNG_FASSUNG, source: "PAPER" },
          });
        }
      }
      if (einwilligung) await tx.contact.update({ where: { id: c.id }, data: { consentAt: new Date() } });

      await tx.contactActivity.create({
        data: {
          contactId: c.id,
          type: vorher ? "geaendert" : "angelegt",
          text: vorher ? `Angaben geändert (${geaendert.length} ${geaendert.length === 1 ? "Feld" : "Felder"})` : "Angelegt",
          createdById: userId,
        },
      });
      await tx.auditLog.create({
        data: {
          clubId,
          userId,
          action: vorher ? "mitglied.geaendert" : "mitglied.angelegt",
          entity: "Contact",
          entityId: c.id,
          data: { felder: geaendert },
        },
      });
      return c.id;
    });
    neuLaden(id);
    return { success: true, id };
  } catch (e) {
    if (e instanceof NichtGefunden) return { error: "Diese Person gibt es in deinem Verein nicht (mehr)." };
    if (typeof e === "object" && e && "code" in e && e.code === "P2002") {
      return { error: "Diese Mitgliedsnummer ist schon vergeben.", field: "memberNumber" };
    }
    throw e;
  }
}

/**
 * Austritt zum Stichtag (Vorgabe heute): Status „ausgetreten“, laufende
 * Beitraege und Gruppen enden, die Historie bleibt.
 */
export async function austrittErfassen(kontaktId: string, stichtag?: string): Promise<ActionResult> {
  if ((await aktuell()).quelle === "muster") return { success: true };
  const z = await schreibZugang();
  if (!z.ok) return { error: z.fehler };
  const { clubId, userId } = z.ctx;
  const { db } = await import("@/lib/db");
  const ende = kalendertag(stichtag ?? null) ?? heuteUtc();

  const vorher = await db.contact.findFirst({ where: { id: kontaktId, clubId }, select: { id: true, status: true } });
  if (!vorher) return { error: "Diese Person gibt es in deinem Verein nicht (mehr)." };
  if (!MITGLIED_STATUS.has(vorher.status) && vorher.status !== "PROSPECT") return { error: "Diese Person ist kein Mitglied." };

  await db.$transaction([
    db.contact.update({ where: { id: kontaktId }, data: { status: "FORMER", exitDate: ende } }),
    db.contactFee.updateMany({ where: { clubId, contactId: kontaktId, OR: [{ validUntil: null }, { validUntil: { gt: ende } }] }, data: { validUntil: ende } }),
    db.groupMembership.updateMany({ where: { contactId: kontaktId, OR: [{ until: null }, { until: { gt: ende } }] }, data: { until: ende } }),
    db.membershipPeriod.updateMany({ where: { clubId, contactId: kontaktId, validUntil: null }, data: { validUntil: ende } }),
    db.membershipPeriod.create({ data: { clubId, contactId: kontaktId, status: "FORMER", validFrom: new Date(ende.getTime() + 86_400_000) } }),
    db.contactActivity.create({ data: { contactId: kontaktId, type: "austritt", text: "Austritt erfasst", createdById: userId } }),
    db.auditLog.create({ data: { clubId, userId, action: "mitglied.austritt", entity: "Contact", entityId: kontaktId, data: { zum: ende.toISOString().slice(0, 10) } } }),
  ]);
  neuLaden(kontaktId);
  return { success: true };
}
